import { BASE_URL } from "../constants";

class TribcoAPI {
  constructor() {
    this.baseURL = BASE_URL;
    this.sessionId = localStorage.getItem("sessionId");
  }

  setSession(sessionId) {
    this.sessionId = sessionId;
    localStorage.setItem("sessionId", sessionId);
  }

  clearSession() {
    this.sessionId = null;
    localStorage.removeItem("sessionId");
    localStorage.removeItem("user");
  }

  // Helper method to add a small delay and validate session context
  async validateSessionContext(minDelay = 100) {
    return new Promise((resolve) => {
      setTimeout(() => {
        const currentSessionId = localStorage.getItem("sessionId");
        const currentUser = localStorage.getItem("user");
        resolve({
          sessionId: currentSessionId,
          user: currentUser ? JSON.parse(currentUser) : null,
        });
      }, minDelay);
    });
  }

  // Method to validate that the current session matches expected user
  async validateSessionForUser(expectedUserId, minDelay = 100) {
    const context = await this.validateSessionContext(minDelay);
    if (context.user && context.user.id !== expectedUserId) {
      throw new Error(
        "Session context mismatch. Please refresh and try again."
      );
    }
    return context;
  }

  async request(endpoint, options = {}) {
    // Always use the latest sessionId from localStorage
    const currentSessionId = localStorage.getItem("sessionId");

    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        "Content-Type": "application/json",
        ...(currentSessionId && { "X-Session-ID": currentSessionId }),
        ...options.headers,
      },
      ...options,
    };
    if (config.body && typeof config.body === "object") {
      config.body = JSON.stringify(config.body);
    }

    const response = await fetch(url, config);

    // Handle unauthorized responses
    if (response.status === 401) {
      // Don't redirect for login endpoints - let the login page handle the error
      if (!endpoint.includes("/admin/login")) {
        // Session might be expired or invalid, clear it
        localStorage.removeItem("sessionId");
        localStorage.removeItem("user");

        // Redirect to login if not already there (avoid hard refresh in React apps)
        if (window.location.pathname !== "/login") {
          // Use pushState to avoid page refresh, let React Router handle navigation
          window.history.pushState(null, null, "/login");
          // Dispatch a custom event to trigger React Router navigation
          window.dispatchEvent(new PopStateEvent("popstate"));
        }

        throw new Error("Session expired. Please login again.");
      }
      // For login endpoints, fall through to normal error handling
    }

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "API request failed");
    }
    return data;
  }

  // Admin Authentication
  async login(email, password) {
    const response = await this.request("/admin/login", {
      method: "POST",
      body: { email, password },
    });
    this.setSession(response.sessionId);
    // Store user data for backward compatibility
    localStorage.setItem("user", JSON.stringify(response.admin));
    return response;
  }

  async logout() {
    await this.request("/admin/logout", { method: "POST" });
    this.clearSession();
  }

  async updatePassword(oldPassword, newPassword) {
    return this.request("/admin/change-password", {
      method: "POST",
      body: { oldPassword, newPassword },
    });
  }

  // Admin Management
  async createAdmin(adminData) {
    return this.request("/admin", {
      method: "POST",
      body: adminData,
    });
  }

  async getAdmins(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/admin?${queryString}`);
  }

  async getAdminsList(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/admin/manage/list?${queryString}`);
  }

  async getCurrentAdmin() {
    // Add a small delay and validate session context to avoid cross-tab contamination
    await this.validateSessionContext(150);

    // Add cache-busting timestamp to ensure fresh data
    const timestamp = Date.now();
    return this.request(`/admin/me?_t=${timestamp}`);
  }

  async createNewAdmin(adminData) {
    return this.request("/admin/create-admin", {
      method: "POST",
      body: adminData,
    });
  }

  async toggleAdminStatus(adminId, isActive) {
    return this.request(`/admin/manage/${adminId}/status`, {
      method: "PATCH",
      body: { isActive },
    });
  }

  // Update admin permissions
  async updateAdminPermissions(adminId, permissions) {
    return this.request(`/admin/manage/${adminId}/permissions`, {
      method: "PUT",
      body: { permissions },
    });
  }

  // Promote admin to super admin
  async promoteToSuperAdmin(adminId) {
    return this.request(`/admin/manage/${adminId}/promote-super-admin`, {
      method: "PUT",
    });
  }

  // Demote super admin to normal admin
  async demoteToAdmin(adminId) {
    return this.request(`/admin/manage/${adminId}/demote-to-admin`, {
      method: "PUT",
    });
  }

  async fixSuperAdminPermissions() {
    return this.request("/admin/fix-permissions", {
      method: "POST",
    });
  }

  // Lead Management
  async createLead(leadData) {
    return this.request("/lead", {
      method: "POST",
      body: leadData,
    });
  }

  async getLeads(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/lead?${queryString}`);
  }

  async getLead(id) {
    return this.request(`/lead/${id}`);
  }

  async updateLeadContact(id, contactData) {
    return this.request(`/lead/${id}/contact`, {
      method: "PUT",
      body: contactData,
    });
  }

  // Unified lead filtering endpoint - supports both simple and advanced filtering
  async getLeadsWithAdvancedFilter(filterState = {}) {
    return this.request("/lead/list", {
      method: "POST",
      body: filterState,
    });
  }

  // Timeline Management
  async getLeadTimeline(leadId, limit = 50) {
    const queryString = new URLSearchParams({ limit }).toString();
    return this.request(`/timeline/lead/${leadId}?${queryString}`);
  }

  async getAdminActivity(adminId, limit = 100) {
    const queryString = new URLSearchParams({ limit }).toString();
    return this.request(`/timeline/admin/${adminId}?${queryString}`);
  }

  async getRecentActivity(limit = 10) {
    const queryString = new URLSearchParams({ limit }).toString();
    return this.request(`/timeline/recent?${queryString}`);
  }

  async createTimelineEntry(timelineData) {
    return this.request("/timeline", {
      method: "POST",
      body: timelineData,
    });
  }

  // Dashboard
  async getDashboard() {
    return this.request("/dashboard");
  }

  // SMART FILTER APIs FOR ADVANCED LEAD FILTERING

  // Get all admin names for filtering by who contacted the lead
  async getAdminNames() {
    return this.request("/lead/filter/admins");
  }

  // Get all unique field values for dropdown filters
  async getLeadFilterOptions() {
    return this.request("/lead/filter/field-options");
  }

  // Get filter metadata (field types, available options, etc.)
  async getFilterMetadata() {
    return this.request("/lead/filter/metadata");
  }

  // Save user's filter presets
  async saveFilterPreset(presetData) {
    return this.request("/admin/filter-presets", {
      method: "POST",
      body: presetData,
    });
  }

  // Get saved filter presets
  async getFilterPresets() {
    return this.request("/admin/filter-presets");
  }

  // Delete filter preset
  async deleteFilterPreset(presetId) {
    return this.request(`/admin/filter-presets/${presetId}`, {
      method: "DELETE",
    });
  }

  // Get statistics for current filtered results
  async getFilteredLeadsStats(filterState = {}) {
    return this.request("/lead/filter-stats", {
      method: "POST",
      body: filterState,
    });
  }

  // Email validation using Abstract API
  async validateEmail(email) {
    return this.request("/email/validate", {
      method: "POST",
      body: { email },
    });
  }

  // Helper methods as mentioned in the API Client Guide

  // Helper method to get high-priority leads only
  async getHighPriorityLeads() {
    const dashboard = await this.getDashboard();
    return dashboard.leadScoring?.filter((lead) => lead.score >= 70) || [];
  }

  // Helper method to get leads needing immediate follow-up
  async getUrgentFollowUps() {
    const dashboard = await this.getDashboard();
    return (
      dashboard.leadScoring?.filter(
        (lead) => lead.urgencyLevel === "High" && lead.dealStatus === "open"
      ) || []
    );
  }

  // Helper method to get sales performance summary
  async getSalesPerformance() {
    const dashboard = await this.getDashboard();
    return {
      conversionRate: dashboard.kpis?.conversionRate,
      avgResponseTime: dashboard.kpis?.avgResponseTime,
      pipelineValue: dashboard.kpis?.potentialRevenue,
      hotLeads: dashboard.kpis?.hotLeads,
      avgDealSize: dashboard.insights?.avgDealSize,
    };
  }

  // Unsubmitted Lead Management
  async createOrUpdateUnsubmittedLead(formData) {
    return this.request("/unsubmitted-lead", {
      method: "POST",
      body: formData,
    });
  }

  async getUnsubmittedLeads(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/unsubmitted-lead?${queryString}`);
  }

  async getUnsubmittedLeadById(id) {
    return this.request(`/unsubmitted-lead/${id}`);
  }

  async deleteUnsubmittedLead(id) {
    return this.request(`/unsubmitted-lead/${id}`, {
      method: "DELETE",
    });
  }

  async getUnsubmittedLeadsStats() {
    return this.request("/unsubmitted-lead/stats");
  }

  // Session Management (Super Admin only)
  async getSessions(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/sessions?${queryString}`);
  }

  async getSessionStats() {
    return this.request("/sessions/stats");
  }

  async suspendSession(sessionId, reason) {
    return this.request(`/sessions/${sessionId}/suspend`, {
      method: "POST",
      body: { reason },
    });
  }

  async resumeSession(sessionId) {
    return this.request(`/sessions/${sessionId}/resume`, {
      method: "POST",
    });
  }

  async terminateSession(sessionId, reason) {
    return this.request(`/sessions/${sessionId}/terminate`, {
      method: "POST",
      body: { reason },
    });
  }

  async bulkTerminateSessions(sessionIds, reason) {
    return this.request("/sessions/bulk-terminate", {
      method: "POST",
      body: { sessionIds, reason },
    });
  }

  async cleanupSessions() {
    return this.request("/sessions/cleanup", { method: "POST" });
  }

  // Export all leads as CSV (Super Admin only)
  async exportLeadsCSV() {
    // This will trigger a file download
    const currentSessionId = localStorage.getItem("sessionId");
    const url = `${this.baseURL}/export/export-csv`;
    const response = await fetch(url, {
      method: "GET",
      headers: {
        ...(currentSessionId && { "X-Session-ID": currentSessionId }),
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Failed to export leads: ${response.status} ${errorText}`
      );
    }
    // Return the CSV blob
    return await response.text();
  }
}

const api = new TribcoAPI();
export default api;
